import { useState } from 'react';
import { usePrinters } from '../../../hooks/usePrinters';
import { usePrinterWebSocket } from '../../../hooks/usePrinterWebSocket';
import { useBusiness } from '../../../contexts/BusinessContext';

export const usePrinterManager = () => {
  const { business } = useBusiness();
  const { 
    testPrinter,
    scanNetwork,
    checkClientStatus,
    status: wsStatus,
    clientStatus,
    error: wsError,
    foundPrinters
  } = usePrinterWebSocket();
  
  const {
    printers: configuredPrinters,
    loading: printersLoading,
    error: printersError,
    addPrinter,
    updatePrinter,
    updatePrinterStatus
  } = usePrinters();

  // Estado local
  const [showConfig, setShowConfig] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [printerConfig, setPrinterConfig] = useState({
    name: '',
    ip_address: '',
    brand: 'epson',
    types: [],
    port: 9100
  });
  const [testingPrinter, setTestingPrinter] = useState(false);
  const [testError, setTestError] = useState(null);

  const validateIPAddress = (ip) => {
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(ip)) return false;
    
    const parts = ip.split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  };

  const handleTestNewPrinter = async () => {
    console.log('=== DEBUG TEST PRINTER ===');
    console.log('Iniciando prueba de impresora');
    console.log('Config:', printerConfig);
    
    setTestError(null);
    
    // Validar IP
    if (!printerConfig.ip_address) {
      console.log('❌ IP vacía');
      setTestError('Ingrese una dirección IP');
      return;
    }
    
    if (!validateIPAddress(printerConfig.ip_address)) {
      console.log('❌ IP inválida:', printerConfig.ip_address);
      setTestError('Dirección IP inválida');
      return;
    }

    console.log('✅ IP válida, intentando conexión...');
    setTestingPrinter(true);
    try {
      const requestBody = {
        printer_id: "test", // ID temporal para prueba
        business_id: business.id,
        content: `=== PRUEBA DE CONEXIÓN ===\n\n` +
                `IP: ${printerConfig.ip_address}\n` +
                `Puerto: ${printerConfig.port}\n` +
                `Marca: ${printerConfig.brand}\n` +
                `Fecha: ${new Date().toLocaleString()}\n\n` +
                `=== FIN DE PRUEBA ===\n\n\n`,
        type: "delivery",
        ip_address: printerConfig.ip_address,
        port: printerConfig.port
      };
      
      console.log('📤 Enviando petición:', {
        url: 'https://tibo-printer-service.fly.dev/print',
        body: requestBody
      });

      // Enviar petición de prueba al printer service usando /print
      const response = await fetch('https://tibo-printer-service.fly.dev/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 Respuesta recibida:', {
        ok: response.ok,
        status: response.status
      });

      if (!response.ok) {
        const error = await response.text();
        console.log('❌ Error en respuesta:', error);
        await updatePrinterStatus(selectedPrinter.id, false);
        throw new Error(error || 'Error al probar la impresora');
      }

      const data = await response.json();
      
      if (data.error) {
        await updatePrinterStatus(selectedPrinter.id, false);
        throw new Error(data.error);
      }

      // Si llegamos aquí, la impresión fue exitosa
      await updatePrinterStatus(selectedPrinter.id, true);
      console.log('✅ Prueba de impresión exitosa');
    } catch (error) {
      console.error('❌ Error probando impresora:', error);
      setTestError(error.message);
    } finally {
      setTestingPrinter(false);
    }
  };

  const handleConfigurePrinter = (printer) => {
    setSelectedPrinter(printer);
    setPrinterConfig({
      name: printer?.name || '',
      ip_address: printer?.ip_address || '',
      brand: printer?.brand || 'epson',
      types: printer?.types || [],
      port: printer?.port || 9100
    });
    setShowConfig(true);
  };

  const handleTestPrinter = async (printerId) => {
    try {
      await testPrinter(printerId);
    } catch (error) {
      console.error('Error testing printer:', error);
    }
  };

  const handleSavePrinter = async () => {
    try {
      const printerData = {
        ...printerConfig,
        business_id: business.id,
        status: 'offline',
        last_seen: new Date().toISOString()
      };

      if (selectedPrinter?.id) {
        await updatePrinter(selectedPrinter.id, printerData);
        console.log('✅ Impresora actualizada:', selectedPrinter.id);
      } else {
        await addPrinter(printerData);
        console.log('✅ Nueva impresora agregada');
      }
      
      setShowConfig(false);
      setPrinterConfig({
        name: '',
        ip_address: '',
        brand: 'epson',
        types: [],
        port: 9100
      });
    } catch (error) {
      console.error('❌ Error guardando impresora:', error);
      throw error;
    }
  };

  const handleCloseConfig = () => {
    setShowConfig(false);
    setSelectedPrinter(null);
    setPrinterConfig({
      name: '',
      ip_address: '',
      brand: 'epson',
      types: [],
      port: 9100
    });
  };

  return {
    // Estado
    showConfig,
    selectedPrinter,
    printerConfig,
    configuredPrinters,
    printersLoading,
    printersError,
    wsStatus,
    clientStatus,
    wsError,
    foundPrinters,
    testingPrinter,
    testError,

    // Handlers
    handleConfigurePrinter,
    handleTestPrinter,
    handleTestNewPrinter,
    handleSavePrinter,
    handleCloseConfig,
    setPrinterConfig,
    scanNetwork,
    checkClientStatus
  };
}; 